import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Input,
  MenuItem,
  Select,
  TextField,
  FormGroup,
  Autocomplete,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { severity_level } from "../../services/constants";
import useFetchRunInstance from "../../hooks/useFetchRunInstance";
import useFetchExecutionSubGroup from "../../hooks/useFetchExecutionSubGroup";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  FeatureType,
  BACKEND_QUERY_PARAMS,
  FE_ONLY_FIELDS,
  getVisibleFields,
  getFieldDescription,
} from "../../config/runParametersConfig";
import {
  IconBtnEditBase,
  IconExistingRunName,
  IconNewRunName,
} from "../../common/utils/icons";
import { addRunInstance } from "../../services/runInstance";
import { addExecutionSubGroup } from "../../services/executionSubGroup";
import {
  addRunInstanceFromExecutionSchema,
  addExecutionSubGroupFromExecutionSchema,
} from "../../schemas";
import { useToast } from "../../services/utils";

interface IRunParameters {
  runParameters: any;
  setRunParameters: Dispatch<SetStateAction<any>>;
  isEditResource: boolean;
  setIsEditResource: Dispatch<SetStateAction<boolean>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  feature: FeatureType;
  // Validate/Rule-only (optional — not used for researchQuery)
  severityColumnNames?: any;
  setSeverityColumnNames?: Dispatch<SetStateAction<any>>;
  isReRun?: any;
  originalUseSecondaryMergeResources?: boolean;
  resourcesData?: any[];
  // Shared optional
  expandedAccordion?: string;
  handleChangeAccordion?: any;
}
const RunParameters = ({
  runParameters,
  setRunParameters,
  isEditResource,
  setIsEditResource,
  setIsLoading,
  feature,
  severityColumnNames,
  setSeverityColumnNames,
  isReRun,
  originalUseSecondaryMergeResources,
  expandedAccordion,
  handleChangeAccordion,
  resourcesData,
}: IRunParameters) => {
  const isShow = getVisibleFields(feature);

  /** Small inline helper: info icon with tooltip, reads description from config */
  const FieldInfo = ({ fieldKey }: { fieldKey: string }) => {
    const desc = getFieldDescription(feature, fieldKey);
    if (!desc) return null;
    return (
      <Tooltip title={desc} arrow placement="top">
        <InfoOutlinedIcon
          sx={{
            fontSize: 16,
            color: "grey.500",
            ml: 0.5,
            verticalAlign: "middle",
            cursor: "pointer",
          }}
        />
      </Tooltip>
    );
  };

  const { showToast } = useToast();
  const [severityColumnFormData, setSeverityColumnFormData] = useState<any>({});
  const [formData, setFormData] = useState<any>({});
  const [runInstance] = useFetchRunInstance({ setIsLoading });
  const [initialFormData, setInitialFormData] = useState<any>({});
  const [initialSeverityColumnFormData, setInitialSeverityColumnFormData] =
    useState<any>({});
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isOldRunName, setIsOldRunName] = useState<boolean>(true);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [newRunName, setNewRunName] = useState<any>("");
  const newRunNameRef = useRef<HTMLInputElement>(null);
  const [instanceData, setInstanceData] = useState<any>([]);
  const [executionSubGroupData] = useFetchExecutionSubGroup({ setIsLoading });
  const [subGroupInstanceData, setSubGroupInstanceData] = useState<any>([]);
  const [isOldSubGroup, setIsOldSubGroup] = useState<boolean>(true);
  const [newSubGroupCode, setNewSubGroupCode] = useState<any>("");
  const newSubGroupCodeRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setInstanceData(runInstance);
  }, [runInstance]);
  useEffect(() => {
    setSubGroupInstanceData(executionSubGroupData);
  }, [executionSubGroupData]);

  // Calculate if Skip Duplicate Records checkbox should be disabled
  const isSkipDuplicateRecordsDisabled = () => {
    if (!resourcesData || !Array.isArray(resourcesData)) {
      return false;
    }
    return resourcesData.length > 2;
  };

  // Get tooltip text for Skip Duplicate Records
  const getSkipDuplicateRecordsTooltip = () => {
    if (!resourcesData || !Array.isArray(resourcesData)) {
      return "Skip duplicate records during rule execution";
    }
    const resourceCount = resourcesData.length;
    if (resourceCount <= 2) {
      return `Skip duplicate records during rule execution. You have ${resourceCount} resource(s), so this option can be changed.`;
    }
    return `Skip duplicate records during rule execution. You have ${resourceCount} resources, so this option is disabled and set to true.`;
  };

  useEffect(() => {
    if (runParameters) {
      // Build formData dynamically from config
      const newFormData: any = {};

      // Sync all backend query param fields from config
      BACKEND_QUERY_PARAMS[feature].forEach((field) => {
        if (field.key === "store_all_data_columns") {
          // Force false when summary_mode is on (non-researchQuery features)
          const canUpdate =
            feature === "researchQuery" || !runParameters?.summary_mode;
          newFormData[field.key] = canUpdate
            ? (runParameters?.store_all_data_columns ?? true)
            : false;
        } else if (field.key === "impact_issues") {
          // Special logic: force false when store_all_data_columns is off
          newFormData[field.key] =
            (runParameters?.store_all_data_columns ?? true)
              ? (runParameters?.impact_issues ?? false)
              : false;
        } else {
          newFormData[field.key] =
            runParameters?.[field.key] ?? field.defaultValue;
        }
      });

      // Sync FE-only fields from config
      const feOnly = FE_ONLY_FIELDS[feature];
      if (feOnly) {
        Object.keys(feOnly).forEach((key) => {
          if (key === "run_instance") {
            newFormData[key] = {
              run_id: runParameters?.run_instance?.run_id || null,
              run_name: runParameters?.run_instance?.run_name || null,
            };
          } else if (key === "execution_sub_group") {
            newFormData[key] = {
              execution_sub_group_id:
                runParameters?.execution_sub_group?.execution_sub_group_id ??
                null,
              execution_sub_group_code:
                runParameters?.execution_sub_group?.execution_sub_group_code ??
                null,
            };
          } else {
            newFormData[key] = runParameters?.[key] ?? feOnly[key];
          }
        });
      }

      setFormData(newFormData);
      setInitialFormData(JSON.parse(JSON.stringify(newFormData)));
      setIsEditResource(false);
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors.resource_loading_max_workers;
        return updatedErrors;
      });
    }
  }, [runParameters]);
  // Update hasChanges when formData or fileData changes
  useEffect(() => {
    const formChanged =
      JSON.stringify(formData) !== JSON.stringify(initialFormData);

    const severityColumnChanged =
      JSON.stringify(severityColumnFormData) !==
      JSON.stringify(initialSeverityColumnFormData);

    setHasChanges(formChanged || severityColumnChanged);
  }, [
    formData,
    severityColumnFormData,
    initialFormData,
    initialSeverityColumnFormData,
  ]);

  useEffect(() => {
    if (severityColumnNames) {
      const newSeverityColumn: any = {
        low: severityColumnNames?.low || null,
        medium: severityColumnNames?.medium || null,
        high: severityColumnNames?.high || null,
      };
      setSeverityColumnFormData(newSeverityColumn);
      setInitialSeverityColumnFormData(newSeverityColumn);
    }
  }, [severityColumnNames]);

  const saveRunParameters = () => {
    const summary_mode_on =
      feature === "researchQuery" ? true : !formData?.summary_mode;
    const store_all_data_columns = summary_mode_on
      ? !!formData?.store_all_data_columns
      : false;
    const impact_issues = store_all_data_columns
      ? !!formData?.impact_issues
      : false;

    // Dynamically build backend query param fields from config
    const backendFields = BACKEND_QUERY_PARAMS[feature];
    const newRunParameters: any = {};
    backendFields.forEach((field) => {
      if (field.key === "impact_issues") {
        newRunParameters[field.key] = impact_issues;
      } else if (field.key === "store_all_data_columns") {
        newRunParameters[field.key] = store_all_data_columns;
      } else if (field.type === "number") {
        newRunParameters[field.key] = formData?.[field.key] || 0;
      } else if (field.type === "boolean") {
        newRunParameters[field.key] =
          formData?.[field.key] ?? field.defaultValue;
      } else {
        newRunParameters[field.key] =
          formData?.[field.key] ?? field.defaultValue;
      }
    });

    // Add FE-only fields
    const feOnly = FE_ONLY_FIELDS[feature];
    if (feOnly) {
      Object.keys(feOnly).forEach((key) => {
        if (key === "run_instance") {
          newRunParameters[key] = {
            run_id: formData?.run_instance?.run_id || 1,
            run_name: formData?.run_instance?.run_name || "Legacy",
          };
        } else if (key === "execution_sub_group") {
          newRunParameters[key] = {
            execution_sub_group_id:
              formData?.execution_sub_group?.execution_sub_group_id ?? 0,
            execution_sub_group_code:
              formData?.execution_sub_group?.execution_sub_group_code ?? "",
          };
        } else {
          newRunParameters[key] = formData?.[key] ?? feOnly[key];
        }
      });
    }
    const newSeverityColumn: any = {
      low: severityColumnFormData?.low || null,
      medium: severityColumnFormData?.medium || null,
      high: severityColumnFormData?.high || null,
    };
    setSeverityColumnNames?.(newSeverityColumn);
    setRunParameters(newRunParameters);
    setIsEditResource(false);
  };
  const handleChangeRunInstance = (e: any, value: any) => {
    setFormData((prevFormData: any) => ({
      ...prevFormData,
      run_instance: {
        run_id: value?.id,
        run_name: value?.run_name,
      },
    }));
  };
  const handleChangeNewRunName = (e: any, value: any) => {
    setNewRunName(value);
    validateField(e.target.name, e.target.value);
  };
  const handleFocusTextField = () => {
    setTimeout(() => {
      if (newRunNameRef.current) {
        newRunNameRef.current.focus();
      }
    }, 50);
  };
  const validateField = async (name: any, value: any) => {
    try {
      const partialFormData = { ...newRunName, [name]: value };
      await addRunInstanceFromExecutionSchema.validateAt(name, partialFormData);
      // If validation passes, clear any previous errors for this field
      setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
    } catch (validationError: any) {
      // If validation fails, set the error message for this field
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };
  const handleSaveRunName = async (e: any) => {
    e.preventDefault();
    const reqBody = {
      run_name: newRunName,
      code: newRunName,
    };
    try {
      await addRunInstanceFromExecutionSchema.validate(reqBody, {
        abortEarly: false,
      });
      addRunInstance({
        payload: reqBody,
      })
        .then((response) => {
          if (response) {
            setFormData((prevFormData: any) => ({
              ...prevFormData,
              run_instance: {
                ...prevFormData.run_instance,
                run_name: response?.run_name,
                run_id: response?.id,
              },
            }));

            setInstanceData((prev: any) => [
              {
                code: response.code,
                created_by: response.created_by,
                created_on: response.created_on,
                description: response.description,
                id: response.id,
                is_active: response.is_active,
                modified_by: response.modified_by,
                run_name: response.run_name,
                updated_on: response.updated_on,
              },
              ...prev,
            ]);
            setIsOldRunName(true);
            showToast(`Execution Group created successfully!`, "success");
          }
        })
        .catch((error) => {
          console.error(`Cannot create  execution group`);
        });
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      validationErrors.inner.forEach(
        (error: { path: string | number; message: string }) => {
          newErrors[error.path] = error.message;
        },
      );
      setErrors((prevErrors) => ({
        ...prevErrors,
        ...newErrors,
      }));
    }
  };
  const handleChangeExecutionSubGroup = (e: any, value: any) => {
    setFormData((prevFormData: any) => ({
      ...prevFormData,
      execution_sub_group: {
        execution_sub_group_id: value?.id,
        execution_sub_group_code:
          value?.execution_sub_group_code || value?.code || "",
      },
    }));
  };
  const handleChangeNewSubGroupCode = (e: any, value: any) => {
    setNewSubGroupCode(value);
    validateSubGroupField(e.target.name, e.target.value);
  };
  const handleFocusSubGroupTextField = () => {
    setTimeout(() => {
      if (newSubGroupCodeRef.current) {
        newSubGroupCodeRef.current.focus();
      }
    }, 50);
  };
  const validateSubGroupField = async (name: any, value: any) => {
    try {
      const partialFormData = { [name]: value };
      await addExecutionSubGroupFromExecutionSchema.validateAt(
        name,
        partialFormData,
      );
      setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
    } catch (validationError: any) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };
  const handleSaveSubGroupCode = async (e: any) => {
    e.preventDefault();
    const reqBody = {
      execution_sub_group_code: newSubGroupCode,
      code: newSubGroupCode,
    };
    try {
      await addExecutionSubGroupFromExecutionSchema.validate(reqBody, {
        abortEarly: false,
      });
      addExecutionSubGroup({
        payload: reqBody,
      })
        .then((response) => {
          if (response) {
            setFormData((prevFormData: any) => ({
              ...prevFormData,
              execution_sub_group: {
                ...prevFormData.execution_sub_group,
                execution_sub_group_code:
                  response?.execution_sub_group_code || response?.code || "",
                execution_sub_group_id: response?.id,
              },
            }));

            setSubGroupInstanceData((prev: any) => [
              {
                code: response.code,
                created_by: response.created_by,
                created_on: response.created_on,
                description: response.description,
                id: response.id,
                is_active: response.is_active,
                modified_by: response.modified_by,
                execution_sub_group_code:
                  response.execution_sub_group_code || response.code || "",
                updated_on: response.updated_on,
              },
              ...prev,
            ]);
            setIsOldSubGroup(true);
            showToast(`Execution Sub Group created successfully!`, "success");
          }
        })
        .catch((error) => {
          console.error(`Cannot create execution sub group`);
        });
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      validationErrors.inner.forEach(
        (error: { path: string | number; message: string }) => {
          newErrors[error.path] = error.message;
        },
      );
      setErrors((prevErrors) => ({
        ...prevErrors,
        ...newErrors,
      }));
    }
  };
  return (
    <>
      <Accordion
        className="mt-6 heading-bold box-shadow color-palette color-palette-2-a"
        expanded={expandedAccordion === "advanced-run-parameters"}
        onChange={handleChangeAccordion("advanced-run-parameters")}
      >
        <AccordionSummary
          aria-controls="panel1d-content"
          id="panel1d-header"
          expandIcon={<ExpandMoreIcon />}
        >
          Advanced Run Parameters
        </AccordionSummary>
        <AccordionDetails sx={{ paddingTop: "16px" }}>
          {!isEditResource ? (
            <>
              <Grid container rowSpacing={1.5} columnSpacing={2.5}>
                {/* ===== ALPHABETICALLY ORDERED FIELDS ===== */}

                {/* Enable Column Slimming (validate/comparison only) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Enable Column Slimming{" "}
                      <FieldInfo fieldKey="enable_column_slimming" />
                    </label>
                    <div className="form-control">
                      {runParameters?.enable_column_slimming ? "True" : "False"}
                    </div>
                  </Grid>
                )}

                {/* Enable Parallel Derived Columns */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="label-text text-bold">
                    Enable Parallel Derived Columns{" "}
                    <FieldInfo fieldKey="enable_parallel_derived_columns" />
                  </label>
                  <div className="form-control">
                    {runParameters?.enable_parallel_derived_columns
                      ? "True"
                      : "False"}
                  </div>
                </Grid>

                {/* Execute Research Queries (rule only) */}
                {isShow.isShowExecuteResearchQueries && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Execute Research Queries{" "}
                      <FieldInfo fieldKey="execute_comparison_research_query" />
                    </label>
                    <div className="form-control">
                      {runParameters?.execute_comparison_research_query
                        ? "True"
                        : "False"}
                    </div>
                  </Grid>
                )}

                {/* Generate Input Files (validate/rule only) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Generate Input Files{" "}
                      <FieldInfo fieldKey="save_input_data_file" />
                    </label>
                    <div className="form-control">
                      {runParameters?.save_input_data_file ? "True" : "False"}
                    </div>
                  </Grid>
                )}

                {/* Generate Output Files */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="label-text text-bold">
                    Generate Output Files{" "}
                    <FieldInfo
                      fieldKey={
                        feature === "researchQuery"
                          ? "generate_output_files"
                          : "generate_files"
                      }
                    />
                  </label>
                  <div className="form-control">
                    {feature === "researchQuery"
                      ? runParameters?.generate_output_files
                        ? "True"
                        : "False"
                      : runParameters?.generate_files
                        ? "True"
                        : "False"}
                  </div>
                </Grid>

                {/* Generate Score */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="label-text text-bold">
                    Generate Score <FieldInfo fieldKey="generate_score" />
                  </label>
                  <div className="form-control">
                    {runParameters?.generate_score ? "True" : "False"}
                  </div>
                </Grid>

                {/* Generate Separate Log File */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="label-text text-bold">
                    Generate Separate Log File{" "}
                    <FieldInfo fieldKey="generate_separate_log_file" />
                  </label>
                  <div className="form-control">
                    {runParameters?.generate_separate_log_file
                      ? "True"
                      : "False"}
                  </div>
                </Grid>

                {/* Summary Mode (validate/rule only) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Summary Mode <FieldInfo fieldKey="summary_mode" />
                    </label>
                    <div className="form-control">
                      {runParameters?.summary_mode ? "True" : "False"}
                    </div>
                  </Grid>
                )}

                {/* Store All Data Columns */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="label-text text-bold">
                    Store All Data Columns{" "}
                    <FieldInfo fieldKey="store_all_data_columns" />
                  </label>
                  <div className="form-control">
                    {(feature === "researchQuery" ||
                      !runParameters?.summary_mode) &&
                    (runParameters?.store_all_data_columns ?? true)
                      ? "True"
                      : "False"}
                  </div>
                </Grid>

                {/* Impact Issues */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="label-text text-bold">
                    Impact Issues <FieldInfo fieldKey="impact_issues" />
                  </label>
                  <div className="form-control">
                    {(runParameters?.store_all_data_columns ?? true)
                      ? (runParameters?.impact_issues ?? false)
                        ? "True"
                        : "False"
                      : "False"}
                  </div>
                </Grid>

                {/* Key Only Mode (validate/rule only) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Key Only Mode <FieldInfo fieldKey="key_only_mode" />
                    </label>
                    <div className="form-control">
                      {runParameters?.key_only_mode ? "True" : "False"}
                    </div>
                  </Grid>
                )}

                {/* Keep Downloaded Files */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="label-text text-bold">
                    Keep Downloaded Files{" "}
                    <FieldInfo fieldKey="keep_downloaded_files" />
                  </label>
                  <div className="form-control">
                    {runParameters?.keep_downloaded_files ? "True" : "False"}
                  </div>
                </Grid>

                {/* No Of Errors In Filter Record Output Files (validate/rule) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      No Of Errors In Filter Record Output Files{" "}
                      <FieldInfo fieldKey="no_of_errors_in_filter_record_output_files" />
                    </label>
                    <div className="form-control">
                      {
                        runParameters?.no_of_errors_in_filter_record_output_files
                      }
                    </div>
                  </Grid>
                )}

                {/* No Of Errors In Output Files (validate/rule) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      No Of Errors In Output Files{" "}
                      <FieldInfo fieldKey="no_of_errors_in_output_files" />
                    </label>
                    <div className="form-control">
                      {runParameters?.no_of_errors_in_output_files}
                    </div>
                  </Grid>
                )}

                {/* No Of Errors In Response (validate/rule) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      No Of Errors In Response{" "}
                      <FieldInfo fieldKey="no_of_errors_in_response" />
                    </label>
                    <div className="form-control">
                      {runParameters?.no_of_errors_in_response}
                    </div>
                  </Grid>
                )}

                {/* No Of Records In Filter Rule Output Files (RQ) */}
                {feature === "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      No Of Records In Filter Rule Output Files{" "}
                      <FieldInfo fieldKey="no_of_records_in_filter_rule_output_files" />
                    </label>
                    <div className="form-control">
                      {runParameters?.no_of_records_in_filter_rule_output_files}
                    </div>
                  </Grid>
                )}

                {/* No Of Records In Output Files (RQ) */}
                {feature === "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      No Of Records In Output Files{" "}
                      <FieldInfo fieldKey="no_of_records_in_output_files" />
                    </label>
                    <div className="form-control">
                      {runParameters?.no_of_records_in_output_files}
                    </div>
                  </Grid>
                )}

                {/* No Of Records In Response (RQ) */}
                {feature === "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      No Of Records In Response{" "}
                      <FieldInfo fieldKey="no_of_records_in_response" />
                    </label>
                    <div className="form-control">
                      {runParameters?.no_of_records_in_response}
                    </div>
                  </Grid>
                )}

                {/* No Of Sample Validation Errors (validate/rule) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      No Of Sample Validation Errors{" "}
                      <FieldInfo fieldKey="no_of_sample_validation_errors" />
                    </label>
                    <div className="form-control">
                      {runParameters?.no_of_sample_validation_errors}
                    </div>
                  </Grid>
                )}

                {/* Pivot Results (rule only) */}
                {feature === "ruleExecution" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Pivot Results <FieldInfo fieldKey="pivot_results" />
                    </label>
                    <div className="form-control">
                      {runParameters?.pivot_results ? "True" : "False"}
                    </div>
                  </Grid>
                )}

                {/* Pull Latest Files */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="label-text text-bold">
                    Pull Latest Files <FieldInfo fieldKey="pull_latest_files" />
                  </label>
                  <div className="form-control">
                    {runParameters?.pull_latest_files ? "True" : "False"}
                  </div>
                </Grid>

                {/* Pull New Files From Server */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="label-text text-bold">
                    Pull New Files From Server{" "}
                    <FieldInfo fieldKey="pull_new_files_from_server" />
                  </label>
                  <div className="form-control">
                    {runParameters?.pull_new_files_from_server
                      ? "True"
                      : "False"}
                  </div>
                </Grid>

                {/* Report Name (RQ only) */}
                {feature === "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Report Name <FieldInfo fieldKey="report_name" />
                    </label>
                    <div className="form-control">
                      {runParameters?.report_name}
                    </div>
                  </Grid>
                )}

                {/* Resource Loading Max Workers (commented out per request)
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="label-text text-bold">
                    Resource Loading Max Workers{" "}
                    <FieldInfo fieldKey="resource_loading_max_workers" />
                  </label>
                  <div className="form-control">
                    {runParameters?.resource_loading_max_workers}
                  </div>
                </Grid>
                */}

                {/* Sampling Validation (validate only) */}
                {feature === "validateResource" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Sampling Validation{" "}
                      <FieldInfo fieldKey="sampling_validation" />
                    </label>
                    <div className="form-control">
                      {runParameters?.sampling_validation ? "True" : "False"}
                    </div>
                  </Grid>
                )}

                {/* Sample Size (validate only, conditional) */}
                {feature === "validateResource" &&
                  runParameters?.sampling_validation && (
                    <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                      <label className="label-text text-bold">
                        Sample Size <FieldInfo fieldKey="sample_size" />
                      </label>
                      <div className="form-control">
                        {runParameters?.sample_size}
                      </div>
                    </Grid>
                  )}

                {/* Save Input Data Files (RQ only) */}
                {feature === "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Save Input Data Files{" "}
                      <FieldInfo fieldKey="save_input_data_files" />
                    </label>
                    <div className="form-control">
                      {runParameters?.save_input_data_files ? "True" : "False"}
                    </div>
                  </Grid>
                )}

                {/* Skip Duplicate Records (validate/rule only) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Skip Duplicate Records{" "}
                      <FieldInfo fieldKey="skip_duplicate_records" />
                    </label>
                    <div className="form-control">
                      {runParameters?.skip_duplicate_records ? "True" : "False"}
                    </div>
                  </Grid>
                )}

                {/* Skip Validation (rule only) */}
                {isShow.isShowSkipValidation && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Skip Validation <FieldInfo fieldKey="skip_validation" />
                    </label>
                    <div className="form-control">
                      {runParameters?.skip_validation ? "True" : "False"}
                    </div>
                  </Grid>
                )}

                {/* Use Merge On Keys As Unique Keys (rule only) */}
                {isShow?.use_secondary_merge_resources && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Use Merge On Keys As Unique Keys{" "}
                      <FieldInfo fieldKey="use_merge_on_keys_as_unique_keys" />
                    </label>
                    <div className="form-control">
                      {runParameters?.use_merge_on_keys_as_unique_keys
                        ? "True"
                        : "False"}
                    </div>
                  </Grid>
                )}

                {/* Use Secondary Merge Resources (rule only) */}
                {isShow?.use_secondary_merge_resources && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Use Secondary Merge Resources{" "}
                      <FieldInfo fieldKey="use_secondary_merge_resources" />
                    </label>
                    <div className="form-control">
                      {runParameters?.use_secondary_merge_resources
                        ? "True"
                        : "False"}
                    </div>
                  </Grid>
                )}

                {/* Validation Severity Level (validate/rule only) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Validation Severity Level
                    </label>
                    <div className="form-control break-word">
                      <div className="text-capitalize">
                        {runParameters?.validation_severity_level}
                      </div>
                    </div>
                  </Grid>
                )}

                {/* Enable Masking */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="label-text text-bold">
                    Enable Masking <FieldInfo fieldKey="is_masking_enabled" />
                  </label>
                  <div className="form-control">
                    {runParameters?.is_masking_enabled ? "True" : "False"}
                  </div>
                </Grid>

                {/* Severity Column Names (validate/rule only) */}
                {feature !== "researchQuery" &&
                runParameters?.isCustomiseSeverity ? (
                  <Grid item xs={12} sm={6} md={6} lg={9} xl={9}>
                    <Box className="label-text text-bold">
                      Severity Column Names
                    </Box>
                    <Grid
                      container
                      rowSpacing={{ lg: 3, md: 0, sm: 0, xs: 0 }}
                      columnSpacing={3}
                    >
                      <Grid item xs={12} sm={12} md>
                        <label className="label-text">
                          <span className="text-bold">Low: </span>
                          {severityColumnFormData?.low || "N/A"}
                        </label>
                      </Grid>
                      <Grid item xs={12} sm={12} md>
                        <label className="label-text">
                          <span className="text-bold">Medium: </span>
                          {severityColumnFormData?.medium || "N/A"}
                        </label>
                      </Grid>
                      <Grid item xs={12} sm={12} md>
                        <label className="label-text">
                          <span className="text-bold">High: </span>
                          {severityColumnFormData?.high || "N/A"}
                        </label>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : null}

                <Grid
                  item
                  xs
                  sx={{ justifyContent: "flex-end", display: "flex" }}
                >
                  <button
                    className="btn-nostyle icon-btn-edit"
                    onClick={() => setIsEditResource(true)}
                  >
                    <IconBtnEditBase ariaLabel="Edit run parameters" />
                  </button>
                </Grid>
              </Grid>
            </>
          ) : (
            <>
              <Grid container rowSpacing={1.5} columnSpacing={2.5}>
                {/* ===== ALPHABETICALLY ORDERED EDIT MODE FIELDS (no info icons) ===== */}

                {/* Enable Column Slimming (validate/comparison only) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="base-resource-checkbox">
                      <Checkbox
                        checked={formData?.enable_column_slimming ?? true}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          setFormData({
                            ...formData,
                            enable_column_slimming: event.target.checked,
                          });
                        }}
                        sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                      />
                      <span>Enable Column Slimming</span>
                    </label>
                  </Grid>
                )}

                {/* Enable Parallel Derived Columns */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="base-resource-checkbox">
                    <Checkbox
                      checked={
                        formData?.enable_parallel_derived_columns ?? false
                      }
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        setFormData({
                          ...formData,
                          enable_parallel_derived_columns: event.target.checked,
                        });
                      }}
                      sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                    />
                    <span>Enable Parallel Derived Columns</span>
                  </label>
                </Grid>

                {/* Execute Research Queries (rule only) */}
                {isShow.isShowExecuteResearchQueries && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="base-resource-checkbox">
                      <Checkbox
                        checked={
                          formData?.execute_comparison_research_query ?? true
                        }
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          setFormData({
                            ...formData,
                            execute_comparison_research_query:
                              event.target.checked,
                          });
                        }}
                        sx={{ "&.Mui-checked": { color: "#196BB4" } }}
                      />
                      <span>Execute Research Queries</span>
                    </label>
                  </Grid>
                )}

                {/* Generate Input Files (validate/rule only) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="base-resource-checkbox">
                      <Checkbox
                        checked={formData?.save_input_data_file}
                        onChange={() => {
                          setFormData((prev: any) => ({
                            ...prev,
                            save_input_data_file:
                              !formData?.save_input_data_file,
                          }));
                        }}
                        sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                      />
                      <span>Generate Input Files</span>
                    </label>
                  </Grid>
                )}

                {/* Generate Output Files */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="base-resource-checkbox">
                    <Checkbox
                      checked={
                        feature === "researchQuery"
                          ? (formData?.generate_output_files ?? true)
                          : formData?.generate_files
                      }
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        setFormData({
                          ...formData,
                          ...(feature === "researchQuery"
                            ? { generate_output_files: event.target.checked }
                            : { generate_files: event.target.checked }),
                        });
                      }}
                      sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                    />
                    <span>Generate Output Files</span>
                  </label>
                </Grid>

                {/* Generate Score */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="base-resource-checkbox">
                    <Checkbox
                      checked={formData?.generate_score ?? true}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        setFormData({
                          ...formData,
                          generate_score: event.target.checked,
                        });
                      }}
                      sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                    />
                    <span>Generate Score</span>
                  </label>
                </Grid>

                {/* Generate Separate Log File */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="base-resource-checkbox">
                    <Checkbox
                      checked={formData?.generate_separate_log_file}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        setFormData({
                          ...formData,
                          generate_separate_log_file: event.target.checked,
                        });
                      }}
                      sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                    />
                    <span>Generate Separate Log File</span>
                  </label>
                </Grid>

                {/* Summary Mode (validate/rule only) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="base-resource-checkbox">
                      <Checkbox
                        checked={formData?.summary_mode}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          const summaryOn = event.target.checked;
                          setFormData((prev: any) => ({
                            ...prev,
                            summary_mode: summaryOn,
                            ...(summaryOn && {
                              store_all_data_columns: false,
                              impact_issues: false,
                            }),
                          }));
                        }}
                        sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                      />
                      <span>Summary Mode</span>
                    </label>
                  </Grid>
                )}

                {/* Store All Data Columns */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="base-resource-checkbox">
                    <Checkbox
                      checked={
                        !!formData?.store_all_data_columns &&
                        (feature === "researchQuery" || !formData?.summary_mode)
                      }
                      disabled={
                        feature !== "researchQuery" && !!formData?.summary_mode
                      }
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        const storeSnapshots = event.target.checked;
                        setFormData((prev: any) => ({
                          ...prev,
                          store_all_data_columns: storeSnapshots,
                          impact_issues: storeSnapshots
                            ? (prev?.impact_issues ?? false)
                            : false,
                        }));
                      }}
                      sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                    />
                    <span>Store All Data Columns</span>
                  </label>
                </Grid>

                {/* Impact Issues */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="base-resource-checkbox">
                    <Checkbox
                      checked={!!formData?.impact_issues}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        setFormData((prev: any) => ({
                          ...prev,
                          impact_issues: event.target.checked,
                        }));
                      }}
                      disabled={!formData?.store_all_data_columns}
                      sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                    />
                    <span>Impact Issues</span>
                  </label>
                </Grid>

                {/* Key Only Mode (validate/rule only) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="base-resource-checkbox">
                      <Checkbox
                        checked={!!formData?.key_only_mode}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          setFormData({
                            ...formData,
                            key_only_mode: event.target.checked,
                          });
                        }}
                        sx={{ "&.Mui-checked": { color: "#196BB4" } }}
                      />
                      <span>Key Only Mode</span>
                    </label>
                  </Grid>
                )}

                {/* Keep Downloaded Files */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="base-resource-checkbox">
                    <Checkbox
                      checked={formData?.keep_downloaded_files}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        setFormData({
                          ...formData,
                          keep_downloaded_files: event.target.checked,
                        });
                      }}
                      sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                    />
                    <span>Keep Downloaded Files</span>
                  </label>
                </Grid>

                {/* No Of Errors In Filter Record Output Files (validate/rule) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <TextField
                      type="number"
                      name="no_of_errors_in_filter_record_output_files"
                      fullWidth
                      label="No Of Errors In Filter Record Output Files"
                      variant="outlined"
                      className="form-control-autocomplete"
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        const inputValue = event.target.value;
                        if (
                          inputValue === "" ||
                          (parseInt(inputValue) >= 0 &&
                            !isNaN(parseInt(inputValue)))
                        ) {
                          setFormData({
                            ...formData,
                            no_of_errors_in_filter_record_output_files:
                              inputValue,
                          });
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                      value={
                        formData?.no_of_errors_in_filter_record_output_files
                      }
                    />
                  </Grid>
                )}

                {/* No Of Errors In Output Files (validate/rule) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <TextField
                      type="number"
                      name="no_of_errors_in_output_files"
                      fullWidth
                      label="No Of Errors In Output Files"
                      variant="outlined"
                      className="form-control-autocomplete"
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        const inputValue = event.target.value;
                        if (
                          inputValue === "" ||
                          (parseInt(inputValue) >= 0 &&
                            !isNaN(parseInt(inputValue)))
                        ) {
                          setFormData({
                            ...formData,
                            no_of_errors_in_output_files: inputValue,
                          });
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                      value={formData?.no_of_errors_in_output_files}
                    />
                  </Grid>
                )}

                {/* No Of Errors In Response (validate/rule) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <TextField
                      type="number"
                      name="no_of_errors_in_response"
                      fullWidth
                      label="No Of Errors In Response"
                      variant="outlined"
                      className="form-control-autocomplete"
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        const inputValue = event.target.value;
                        if (
                          inputValue === "" ||
                          (parseInt(inputValue) >= 0 &&
                            !isNaN(parseInt(inputValue)))
                        ) {
                          setFormData({
                            ...formData,
                            no_of_errors_in_response: inputValue,
                          });
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                      value={formData?.no_of_errors_in_response}
                    />
                  </Grid>
                )}

                {/* No Of Records In Filter Rule Output Files (RQ) */}
                {feature === "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <TextField
                      type="number"
                      name="no_of_records_in_filter_rule_output_files"
                      fullWidth
                      label="No Of Records In Filter Rule Output Files"
                      variant="outlined"
                      className="form-control-autocomplete"
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        const inputValue = event.target.value;
                        if (
                          inputValue === "" ||
                          (parseInt(inputValue) >= 0 &&
                            !isNaN(parseInt(inputValue)))
                        ) {
                          setFormData({
                            ...formData,
                            no_of_records_in_filter_rule_output_files:
                              inputValue,
                          });
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                      value={
                        formData?.no_of_records_in_filter_rule_output_files
                      }
                    />
                  </Grid>
                )}

                {/* No Of Records In Output Files (RQ) */}
                {feature === "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <TextField
                      type="number"
                      name="no_of_records_in_output_files"
                      fullWidth
                      label="No Of Records In Output Files"
                      variant="outlined"
                      className="form-control-autocomplete"
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        const inputValue = event.target.value;
                        if (
                          inputValue === "" ||
                          (parseInt(inputValue) >= 0 &&
                            !isNaN(parseInt(inputValue)))
                        ) {
                          setFormData({
                            ...formData,
                            no_of_records_in_output_files: inputValue,
                          });
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                      value={formData?.no_of_records_in_output_files}
                    />
                  </Grid>
                )}

                {/* No Of Records In Response (RQ) */}
                {feature === "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <TextField
                      type="number"
                      name="no_of_records_in_response"
                      fullWidth
                      label="No Of Records In Response"
                      variant="outlined"
                      className="form-control-autocomplete"
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        const inputValue = event.target.value;
                        if (
                          inputValue === "" ||
                          (parseInt(inputValue) >= 0 &&
                            !isNaN(parseInt(inputValue)))
                        ) {
                          setFormData({
                            ...formData,
                            no_of_records_in_response: inputValue,
                          });
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                      value={formData?.no_of_records_in_response}
                    />
                  </Grid>
                )}

                {/* No Of Sample Validation Errors (validate/rule) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <TextField
                      type="number"
                      name="no_of_sample_validation_errors"
                      fullWidth
                      label="No Of Sample Validation Errors"
                      variant="outlined"
                      className="form-control-autocomplete"
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        const inputValue = event.target.value;
                        if (
                          inputValue === "" ||
                          (parseInt(inputValue) >= 0 &&
                            !isNaN(parseInt(inputValue)))
                        ) {
                          setFormData({
                            ...formData,
                            no_of_sample_validation_errors: inputValue,
                          });
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                      value={formData?.no_of_sample_validation_errors}
                    />
                  </Grid>
                )}

                {/* Pivot Results (rule only) */}
                {feature === "ruleExecution" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="base-resource-checkbox">
                      <Checkbox
                        checked={formData?.pivot_results ?? false}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          setFormData({
                            ...formData,
                            pivot_results: event.target.checked,
                          });
                        }}
                        sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                      />
                      <span>Pivot Results</span>
                    </label>
                  </Grid>
                )}

                {/* Pull Latest Files */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="base-resource-checkbox">
                    <Checkbox
                      checked={formData?.pull_latest_files}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        setFormData({
                          ...formData,
                          pull_latest_files: event.target.checked,
                        });
                      }}
                      sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                    />
                    <span>Pull Latest Files</span>
                  </label>
                </Grid>

                {/* Pull New Files From Server */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="base-resource-checkbox">
                    <Checkbox
                      checked={formData?.pull_new_files_from_server}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        setFormData({
                          ...formData,
                          pull_new_files_from_server: event.target.checked,
                        });
                      }}
                      sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                    />
                    <span>Pull New Files From Server</span>
                  </label>
                </Grid>

                {/* Report Name (RQ only) */}
                {feature === "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <TextField
                      type="text"
                      name="report_name"
                      fullWidth
                      label="Report Name"
                      variant="outlined"
                      className="form-control-autocomplete"
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        setFormData({
                          ...formData,
                          report_name: event.target.value,
                        });
                      }}
                      InputLabelProps={{ shrink: true }}
                      value={formData?.report_name || ""}
                    />
                  </Grid>
                )}

                {/* Resource Loading Max Workers (commented out per request)
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <TextField
                    type="number"
                    name="resource_loading_max_workers"
                    fullWidth
                    label="Resource Loading Max Workers"
                    variant="outlined"
                    className="form-control-autocomplete"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const inputValue = event.target.value;
                      if (
                        inputValue === "" ||
                        (parseInt(inputValue) >= 0 &&
                          !isNaN(parseInt(inputValue)))
                      ) {
                        setFormData({
                          ...formData,
                          resource_loading_max_workers: inputValue,
                        });
                        const numValue = parseInt(inputValue, 10);
                        setErrors((prevErrors) => ({
                          ...prevErrors,
                          resource_loading_max_workers:
                            inputValue === "" ||
                            numValue < 1 ||
                            numValue > 4
                              ? "Value must be between 1 and 4"
                              : "",
                        }));
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: 1, max: 4 }}
                    value={formData?.resource_loading_max_workers}
                    error={!!errors?.resource_loading_max_workers}
                    helperText={errors?.resource_loading_max_workers}
                  />
                </Grid>
                */}

                {/* Sampling Validation (validate only) */}
                {feature === "validateResource" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="base-resource-checkbox">
                      <Checkbox
                        checked={formData?.sampling_validation ?? false}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          setFormData({
                            ...formData,
                            sampling_validation: event.target.checked,
                            ...(event.target.checked ? {} : { sample_size: 0 }),
                          });
                        }}
                        sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                      />
                      <span>Sampling Validation</span>
                    </label>
                  </Grid>
                )}

                {/* Sample Size (validate only, conditional) */}
                {feature === "validateResource" &&
                  formData?.sampling_validation && (
                    <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                      <TextField
                        type="number"
                        name="sample_size"
                        fullWidth
                        label="Sample Size"
                        variant="outlined"
                        className="form-control-autocomplete"
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          const inputValue = event.target.value;
                          if (
                            inputValue === "" ||
                            (parseInt(inputValue) >= 0 &&
                              !isNaN(parseInt(inputValue)))
                          ) {
                            setFormData({
                              ...formData,
                              sample_size: inputValue,
                            });
                          }
                        }}
                        InputLabelProps={{ shrink: true }}
                        value={formData?.sample_size}
                      />
                    </Grid>
                  )}

                {/* Save Input Data Files (RQ only) */}
                {feature === "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="base-resource-checkbox">
                      <Checkbox
                        checked={formData?.save_input_data_files ?? false}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          setFormData({
                            ...formData,
                            save_input_data_files: event.target.checked,
                          });
                        }}
                        sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                      />
                      <span>Save Input Data Files</span>
                    </label>
                  </Grid>
                )}

                {/* Skip Duplicate Records (validate/rule only) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="base-resource-checkbox">
                      <Checkbox
                        checked={formData?.skip_duplicate_records}
                        disabled={isSkipDuplicateRecordsDisabled()}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          setFormData({
                            ...formData,
                            skip_duplicate_records: event.target.checked,
                          });
                        }}
                        sx={{
                          "&.Mui-checked": { color: "#537ea0" },
                          "&.Mui-disabled": { color: "#BDBDBD", opacity: 0.6 },
                          "&.Mui-checked.Mui-disabled": {
                            color: "#9E9E9E",
                            opacity: 0.6,
                          },
                        }}
                      />
                      <span>Skip Duplicate Records</span>
                    </label>
                  </Grid>
                )}

                {/* Skip Validation (rule only) */}
                {isShow.isShowSkipValidation && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="base-resource-checkbox">
                      <Checkbox
                        checked={formData?.skip_validation}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          setFormData({
                            ...formData,
                            skip_validation: event.target.checked,
                          });
                        }}
                        sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                      />
                      <span>Skip Validation</span>
                    </label>
                  </Grid>
                )}

                {/* Use Merge On Keys As Unique Keys (rule only) */}
                {isShow?.use_secondary_merge_resources && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="base-resource-checkbox">
                      <Checkbox
                        checked={
                          formData?.use_merge_on_keys_as_unique_keys ?? true
                        }
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          setFormData({
                            ...formData,
                            use_merge_on_keys_as_unique_keys:
                              event.target.checked,
                          });
                        }}
                        sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                      />
                      <span>Use Merge On Keys As Unique Keys</span>
                    </label>
                  </Grid>
                )}

                {/* Use Secondary Merge Resources (rule only) */}
                {isShow?.use_secondary_merge_resources && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="base-resource-checkbox">
                      <Checkbox
                        checked={
                          formData?.use_secondary_merge_resources ?? true
                        }
                        disabled={
                          isReRun && originalUseSecondaryMergeResources === false
                        }
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          setFormData({
                            ...formData,
                            use_secondary_merge_resources: event.target.checked,
                          });
                        }}
                        sx={{ "&.Mui-checked": { color: "#196BB4" } }}
                      />
                      <span>Use Secondary Merge Resources</span>
                    </label>
                  </Grid>
                )}

                {/* Validation Severity Level (validate/rule only) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <Box
                      sx={{ display: { md: "block", sm: "none", xs: "none" } }}
                      className="label-text line-height16"
                    >
                      Validation Severity Level
                    </Box>
                    <Select
                      className="white-space-nowrap form-control"
                      value={
                        formData?.validation_severity_level
                          ?.charAt(0)
                          .toUpperCase() +
                        formData?.validation_severity_level
                          ?.slice(1)
                          .toLowerCase()
                      }
                      placeholder="Select..."
                      style={{ width: "100%", height: 35 }}
                      onChange={(event) => {
                        setFormData({
                          ...formData,
                          validation_severity_level: event.target.value,
                        });
                      }}
                      displayEmpty
                      input={<Input />}
                      renderValue={(selected) => (
                        <span>{selected || "Severity Level"}</span>
                      )}
                    >
                      {severity_level.map((severity: string) => (
                        <MenuItem key={severity} value={severity}>
                          <span>
                            {severity.charAt(0).toUpperCase() +
                              severity.slice(1).toLowerCase()}
                          </span>
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                )}

                {/* Customize Severity Column Names (validate/rule only) */}
                {feature !== "researchQuery" && (
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="base-resource-checkbox">
                      <Checkbox
                        checked={formData?.isCustomiseSeverity}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          setFormData((prev: any) => ({
                            ...prev,
                            isCustomiseSeverity: event.target.checked,
                          }));
                          if (!event.target.checked) {
                            setSeverityColumnNames?.({
                              low: null,
                              medium: null,
                              high: null,
                            });
                          }
                        }}
                        sx={{
                          "&.Mui-checked": {
                            color: "#537ea0",
                          },
                        }}
                      />
                      <span>Customize Severity Column Names</span>
                    </label>
                  </Grid>
                )}
                {feature !== "researchQuery" &&
                formData?.isCustomiseSeverity ? (
                  <Grid item xs={12} sm={6} md={12} lg={12} xl={12}>
                    <Box
                      sx={{
                        display: { md: "block", sm: "none", xs: "none" },
                      }}
                      className="label-text"
                    >
                      &nbsp;
                    </Box>
                    <Box
                      sx={{
                        display: { md: "block", sm: "none", xs: "none" },
                      }}
                      className="label-text"
                    >
                      Severity Column Names
                    </Box>
                    <Grid container rowSpacing={3} columnSpacing={3}>
                      <Grid item xs>
                        <FormGroup row className="form-group-row">
                          <Button variant="contained" disableElevation>
                            <span>Low</span>
                          </Button>
                          <TextField
                            type="text"
                            name="low"
                            variant="outlined"
                            className="form-control-autocomplete"
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              setSeverityColumnFormData({
                                ...severityColumnFormData,
                                low: event.target.value,
                              });
                            }}
                            // placeholder="Ex: Sample severity column name"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            value={severityColumnFormData?.low || ""}
                          />
                        </FormGroup>
                      </Grid>
                      <Grid item xs>
                        <FormGroup row className="form-group-row">
                          <Button variant="contained" disableElevation>
                            <span>Medium</span>
                          </Button>
                          <TextField
                            type="text"
                            name="medium"
                            fullWidth
                            variant="outlined"
                            className="form-control-autocomplete"
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              setSeverityColumnFormData({
                                ...severityColumnFormData,
                                medium: event.target.value,
                              });
                            }}
                            // placeholder="Ex: Sample severity column name"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            value={severityColumnFormData?.medium || ""}
                          />
                        </FormGroup>
                      </Grid>
                      <Grid item xs>
                        <FormGroup row className="form-group-row">
                          <Button variant="contained" disableElevation>
                            <span>High</span>
                          </Button>
                          <TextField
                            type="text"
                            name="high"
                            fullWidth
                            variant="outlined"
                            className="form-control-autocomplete"
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              setSeverityColumnFormData({
                                ...severityColumnFormData,
                                high: event.target.value,
                              });
                            }}
                            // placeholder="Ex: Sample severity column name"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            value={severityColumnFormData?.high || ""}
                          />
                        </FormGroup>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : null}

                {/* Enable Masking */}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="base-resource-checkbox">
                    <Checkbox
                      checked={formData?.is_masking_enabled ?? false}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        setFormData({
                          ...formData,
                          is_masking_enabled: event.target.checked,
                        });
                      }}
                      sx={{ "&.Mui-checked": { color: "#196BB4" } }}
                    />
                    <span>Enable Masking</span>
                  </label>
                </Grid>

                <Grid
                  item
                  xs
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "flex-end",
                    columnGap: "20px",
                  }}
                >
                  <Button
                    color="secondary"
                    variant="contained"
                    onClick={() => {
                      setFormData(JSON.parse(JSON.stringify(initialFormData)));
                      setSeverityColumnFormData(
                        JSON.parse(
                          JSON.stringify(initialSeverityColumnFormData),
                        ),
                      );
                      setRunParameters(
                        JSON.parse(JSON.stringify(initialFormData)),
                      );
                      setIsEditResource(false);
                    }}
                    className="btn-orange btn-dark"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    color="secondary"
                    variant="contained"
                    className="btn-orange"
                    onClick={saveRunParameters}
                    disabled={
                      !hasChanges || !!errors?.resource_loading_max_workers
                    }
                  >
                    <SaveOutlinedIcon /> &nbsp; Save
                  </Button>
                </Grid>
              </Grid>
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default RunParameters;
